import { STATES } from "./states";

export interface Game {
  id: string;
  name: string;
  slug: string;
  is_multistate: boolean;
  state_id: string | null;
  number_count_main: number;
  number_range_main: number;
  number_count_bonus: number;
  number_range_bonus: number;
  has_multiplier: boolean;
  ticket_price: number;
  draw_schedule: string;
  description?: string;
  odds_info?: string;
  logo_url?: string | null;
  states?: { id: string; name: string; slug: string; abbreviation: string; official_lottery_url?: string | null };
}

export const MULTISTATE_GAMES: Game[] = [
  {
    id: "game-powerball",
    name: "Powerball",
    slug: "powerball",
    is_multistate: true,
    state_id: null,
    number_count_main: 5,
    number_range_main: 69,
    number_count_bonus: 1,
    number_range_bonus: 26,
    has_multiplier: true,
    ticket_price: 2,
    draw_schedule: "Mon, Wed, Sat 10:59 PM ET",
    description: "Powerball is one of America's largest multi-state lottery games. Pick 5 white balls (1–69) and 1 red Powerball (1–26).",
    odds_info: "Jackpot odds: 1 in 292,201,338",
  },
  {
    id: "game-mega-millions",
    name: "Mega Millions",
    slug: "mega-millions",
    is_multistate: true,
    state_id: null,
    number_count_main: 5,
    number_range_main: 70,
    number_count_bonus: 1,
    number_range_bonus: 24,
    has_multiplier: true,
    ticket_price: 5,
    draw_schedule: "Tue, Fri 11:00 PM ET",
    description: "Mega Millions: pick 5 white balls (1–70) and 1 Mega Ball (1–24). The built-in Multiplier multiplies non-jackpot prizes.",
    odds_info: "Jackpot odds: 1 in 290,472,336",
  },
  {
    id: "game-lucky-for-life",
    name: "Lucky for Life",
    slug: "lucky-for-life",
    is_multistate: true,
    state_id: null,
    number_count_main: 5,
    number_range_main: 48,
    number_count_bonus: 1,
    number_range_bonus: 18,
    has_multiplier: false,
    ticket_price: 2,
    draw_schedule: "Daily 10:38 PM ET",
    description: "Match all 6 numbers to win $1,000 a day for life.",
    odds_info: "Top prize odds: 1 in 30,821,472",
  },
  {
    id: "game-cash4life",
    name: "Cash4Life",
    slug: "cash4life",
    is_multistate: true,
    state_id: null,
    number_count_main: 5,
    number_range_main: 60,
    number_count_bonus: 1,
    number_range_bonus: 4,
    has_multiplier: false,
    ticket_price: 2,
    draw_schedule: "Daily 9:00 PM ET",
    description: "Win $1,000 a day for life by matching 5 numbers + the Cash Ball.",
    odds_info: "Top prize odds: 1 in 21,846,048",
  },
  {
    id: "game-lotto-america",
    name: "Lotto America",
    slug: "lotto-america",
    is_multistate: true,
    state_id: null,
    number_count_main: 5,
    number_range_main: 52,
    number_count_bonus: 1,
    number_range_bonus: 10,
    has_multiplier: true,
    ticket_price: 1,
    draw_schedule: "Mon, Wed, Sat 10:00 PM ET",
    description: "Pick 5 of 52 white balls + 1 Star Ball (1–10). Jackpot starts at $2 million.",
    odds_info: "Jackpot odds: 1 in 25,989,600",
  },
];

MULTISTATE_GAMES.push({
  id: "game-powerball-double-play",
  name: "Powerball Double Play",
  slug: "powerball-double-play",
  is_multistate: true,
  state_id: null,
  number_count_main: 5,
  number_range_main: 69,
  number_count_bonus: 1,
  number_range_bonus: 26,
  has_multiplier: false,
  ticket_price: 1,
  draw_schedule: "Mon, Wed, Sat 11:00 PM ET",
  description: "Powerball Double Play add-on giving tickets a second drawing for cash prizes up to $10 million.",
  odds_info: "Top prize odds: 1 in 292,201,338",
});

MULTISTATE_GAMES.push({
  id: "game-2by2",
  name: "2by2",
  slug: "2by2",
  is_multistate: true,
  state_id: null,
  number_count_main: 2,
  number_range_main: 26,
  number_count_bonus: 2,
  number_range_bonus: 26,
  has_multiplier: false,
  ticket_price: 1,
  draw_schedule: "Daily 9:30 PM CT",
  description: "Multi-state 2by2 (Kansas, Nebraska, North Dakota). Pick 2 red and 2 white numbers (1–26) for a top prize of $22,000.",
  odds_info: "Top prize odds: 1 in 105,625",
});

function stateGame(stateAbbr: string, suffix: string, name: string, opts: Partial<Game>): Game {
  const state = STATES.find((s) => s.abbreviation === stateAbbr)!;
  return {
    id: `game-${state.slug}-${suffix}`,
    name,
    slug: `${state.slug}-${suffix}`,
    is_multistate: false,
    state_id: state.id,
    number_count_main: 5,
    number_range_main: 39,
    number_count_bonus: 0,
    number_range_bonus: 0,
    has_multiplier: false,
    ticket_price: 1,
    draw_schedule: "Daily",
    states: state,
    ...opts,
  };
}

export const STATE_GAMES: Game[] = [
  stateGame("NY", "lotto", "NY Lotto", {
    number_count_main: 6, number_range_main: 59, draw_schedule: "Wed, Sat 8:15 PM ET", ticket_price: 1,
    description: "Pick 6 of 59 to win NY Lotto.",
  }),
  stateGame("NY", "take-5-midday", "Take 5 Midday", {
    number_count_main: 5, number_range_main: 39, draw_schedule: "Daily 2:30 PM ET",
  }),
  stateGame("NY", "take-5-evening", "Take 5 Evening", {
    number_count_main: 5, number_range_main: 39, draw_schedule: "Daily 10:30 PM ET",
  }),
  stateGame("CA", "superlotto-plus", "California SuperLotto Plus", {
    number_count_main: 5, number_range_main: 47, number_count_bonus: 1, number_range_bonus: 27,
    draw_schedule: "Wed, Sat 7:57 PM PT", ticket_price: 1,
    description: "Pick 5 of 47 plus a Mega number 1–27.",
  }),
  stateGame("CA", "fantasy-5", "Fantasy 5", {
    number_count_main: 5, number_range_main: 39, draw_schedule: "Daily 6:30 PM PT",
  }),
  stateGame("CA", "daily-derby", "Daily Derby", {
    number_count_main: 3, number_range_main: 12, draw_schedule: "Daily 6:30 PM PT", ticket_price: 2,
    description: "California Daily Derby — pick the 1st, 2nd and 3rd place horses (1–12) plus the race time for a top prize starting at $50,000.",
  }),
  stateGame("TX", "lotto-texas", "Texas Lotto", {
    number_count_main: 6, number_range_main: 54, draw_schedule: "Mon, Wed, Sat 10:12 PM CT", ticket_price: 1,
  }),
  // Texas All or Nothing — 4 daily draws
  stateGame("TX", "all-or-nothing-morning", "All or Nothing Morning", {
    number_count_main: 12, number_range_main: 24, draw_schedule: "Mon-Sat 10:00 AM CT", ticket_price: 2,
  }),
  stateGame("TX", "all-or-nothing-day", "All or Nothing Day", {
    number_count_main: 12, number_range_main: 24, draw_schedule: "Mon-Sat 12:27 PM CT", ticket_price: 2,
  }),
  stateGame("TX", "all-or-nothing-evening", "All or Nothing Evening", {
    number_count_main: 12, number_range_main: 24, draw_schedule: "Mon-Sat 6:00 PM CT", ticket_price: 2,
  }),
  stateGame("TX", "all-or-nothing-night", "All or Nothing Night", {
    number_count_main: 12, number_range_main: 24, draw_schedule: "Mon-Sat 10:12 PM CT", ticket_price: 2,
  }),
  stateGame("TX", "cash-five", "Cash Five", {
    number_count_main: 5, number_range_main: 35, draw_schedule: "Mon-Sat 10:00 PM CT",
  }),
  stateGame("FL", "florida-lotto", "Florida Lotto", {
    number_count_main: 6, number_range_main: 53, draw_schedule: "Wed, Sat 11:15 PM ET", ticket_price: 2,
  }),
  stateGame("FL", "fantasy-5-midday", "Fantasy 5 Midday", {
    number_count_main: 5, number_range_main: 36, draw_schedule: "Daily 1:05 PM ET",
  }),
  stateGame("FL", "fantasy-5-evening", "Fantasy 5 Evening", {
    number_count_main: 5, number_range_main: 36, draw_schedule: "Daily 11:15 PM ET",
  }),
  stateGame("FL", "double-play", "Double Play", {
    number_count_main: 5, number_range_main: 69, number_count_bonus: 1, number_range_bonus: 26,
    draw_schedule: "Mon, Wed, Sat 10:59 PM ET", ticket_price: 1,
    description: "Powerball Double Play add-on giving tickets a second drawing for cash prizes up to $10 million.",
  }),
  stateGame("FL", "cash-pop-morning", "Cash Pop Morning", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 8:30 AM ET", ticket_price: 1 }),
  stateGame("FL", "cash-pop-matinee", "Cash Pop Matinee", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 12:30 PM ET", ticket_price: 1 }),
  stateGame("FL", "cash-pop-afternoon", "Cash Pop Afternoon", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 4:30 PM ET", ticket_price: 1 }),
  stateGame("FL", "cash-pop-evening", "Cash Pop Evening", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 8:30 PM ET", ticket_price: 1 }),
  stateGame("FL", "cash-pop-late-night", "Cash Pop Late Night", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 11:59 PM ET", ticket_price: 1 }),
  stateGame("IL", "lotto", "Illinois Lotto", {
    number_count_main: 6, number_range_main: 50, number_count_bonus: 1, number_range_bonus: 50,
    draw_schedule: "Mon, Thu, Sat 9:22 PM CT", ticket_price: 2,
  }),
  stateGame("PA", "match-6", "Match 6 Lotto", {
    number_count_main: 6, number_range_main: 49, draw_schedule: "Daily 6:59 PM ET", ticket_price: 2,
  }),
  stateGame("OH", "classic-lotto", "Classic Lotto", {
    number_count_main: 6, number_range_main: 49, draw_schedule: "Mon, Wed, Sat 7:05 PM ET", ticket_price: 1,
  }),
  stateGame("GA", "fantasy-5", "Fantasy 5", {
    number_count_main: 5, number_range_main: 42, draw_schedule: "Daily 11:34 PM ET",
  }),
  stateGame("GA", "cash-pop-early-bird", "Cash Pop Early Bird", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 10:30 AM ET", ticket_price: 1 }),
  stateGame("GA", "cash-pop-matinee", "Cash Pop Matinee", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 1:30 PM ET", ticket_price: 1 }),
  stateGame("GA", "cash-pop-drive-time", "Cash Pop Drive Time", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 5:30 PM ET", ticket_price: 1 }),
  stateGame("GA", "cash-pop-primetime", "Cash Pop Primetime", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 9:30 PM ET", ticket_price: 1 }),
  stateGame("GA", "cash-pop-night-owl", "Cash Pop Night Owl", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 11:59 PM ET", ticket_price: 1 }),
  stateGame("MI", "lotto-47", "Classic Lotto 47", {
    number_count_main: 6, number_range_main: 47, draw_schedule: "Wed, Sat 7:29 PM ET", ticket_price: 1,
  }),
  stateGame("MI", "fantasy-5", "Fantasy 5", {
    number_count_main: 5, number_range_main: 39, draw_schedule: "Daily 7:29 PM ET", ticket_price: 1,
  }),
  stateGame("MI", "fantasy-5-double-play", "Fantasy 5 Double Play", {
    number_count_main: 5, number_range_main: 39, draw_schedule: "Daily 7:29 PM ET", ticket_price: 1,
    description: "Fantasy 5 Double Play add-on gives an extra drawing for a chance at a second prize.",
  }),
  stateGame("MI", "lotto-double-play", "Lotto Double Play", {
    number_count_main: 6, number_range_main: 47, draw_schedule: "Wed, Sat 7:29 PM ET", ticket_price: 1,
    description: "Classic Lotto 47 Double Play add-on gives a second nightly drawing for fixed cash prizes.",
  }),
  stateGame("MI", "poker-lotto", "Poker Lotto", {
    number_count_main: 5, number_range_main: 52, draw_schedule: "Daily 7:29 PM ET", ticket_price: 2,
    description: "Pick 5 cards from a 52-card deck for instant poker-hand prizes plus the nightly Poker Lotto All In drawing.",
  }),
  stateGame("NC", "cash-5", "Cash 5", {
    number_count_main: 5, number_range_main: 43, draw_schedule: "Daily 11:22 PM ET",
  }),
  stateGame("NC", "double-play", "Double Play", {
    number_count_main: 5, number_range_main: 69, number_count_bonus: 1, number_range_bonus: 26,
    draw_schedule: "Mon, Wed, Sat 10:59 PM ET", ticket_price: 1,
    description: "Powerball Double Play add-on giving tickets a second drawing for cash prizes up to $10 million.",
  }),
  stateGame("NC", "cash-pop-morning-buzz", "Cash Pop Morning Buzz", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 8:30 AM ET", ticket_price: 1 }),
  stateGame("NC", "cash-pop-lunch-rush", "Cash Pop Lunch Rush", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 12:30 PM ET", ticket_price: 1 }),
  stateGame("NC", "cash-pop-clock-out-cash", "Cash Pop Clock Out Cash", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 5:30 PM ET", ticket_price: 1 }),
  stateGame("NC", "cash-pop-primetime-pop", "Cash Pop Primetime Pop", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 9:30 PM ET", ticket_price: 1 }),
  stateGame("NC", "cash-pop-midnight-money", "Cash Pop Midnight Money", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 11:59 PM ET", ticket_price: 1 }),
  stateGame("VA", "cash-5", "Cash 5", {
    number_count_main: 5, number_range_main: 41, draw_schedule: "Daily 11:00 PM ET",
  }),
  stateGame("VA", "bank-a-million", "Bank a Million", {
    number_count_main: 6, number_range_main: 40, number_count_bonus: 1, number_range_bonus: 6,
    draw_schedule: "Wed, Sat 11:00 PM ET", ticket_price: 1,
  }),
  stateGame("VA", "cash-pop-coffee-break", "Cash Pop Coffee Break", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 9:00 AM ET", ticket_price: 1 }),
  stateGame("VA", "cash-pop-lunch-break", "Cash Pop Lunch Break", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 12:00 PM ET", ticket_price: 1 }),
  stateGame("VA", "cash-pop-rush-hour", "Cash Pop Rush Hour", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 5:00 PM ET", ticket_price: 1 }),
  stateGame("VA", "cash-pop-prime-time", "Cash Pop Prime Time", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 8:00 PM ET", ticket_price: 1 }),
  stateGame("VA", "cash-pop-after-hours", "Cash Pop After Hours", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 11:00 PM ET", ticket_price: 1 }),
  stateGame("MA", "megabucks", "Megabucks", {
    number_count_main: 6, number_range_main: 49, draw_schedule: "Wed, Sat 9:00 PM ET", ticket_price: 1,
    description: "Massachusetts Megabucks Doubler — pick 6 of 49 with a chance to double non-jackpot prizes.",
  }),
  stateGame("MA", "mass-cash-midday", "Mass Cash Midday", {
    number_count_main: 5, number_range_main: 35, draw_schedule: "Daily 12:00 PM ET", ticket_price: 1,
  }),
  stateGame("MA", "masscash-evening", "MassCash Evening", {
    number_count_main: 5, number_range_main: 35, draw_schedule: "Daily 9:00 PM ET", ticket_price: 1,
  }),
  stateGame("NJ", "pick-6", "Pick 6 Lotto", {
    number_count_main: 6, number_range_main: 49, draw_schedule: "Mon, Thu 10:57 PM ET", ticket_price: 2,
  }),
  stateGame("AZ", "the-pick", "The Pick", {
    number_count_main: 6, number_range_main: 44, draw_schedule: "Wed, Sat 7:00 PM MT", ticket_price: 1,
  }),
  stateGame("AZ", "fantasy-5", "Fantasy 5", {
    number_count_main: 5, number_range_main: 41, draw_schedule: "Daily 7:00 PM MT", ticket_price: 1,
  }),
  stateGame("AZ", "triple-twist", "Triple Twist", {
    number_count_main: 6, number_range_main: 47, draw_schedule: "Wed, Sat 7:00 PM MT", ticket_price: 1,
  }),
  stateGame("WA", "hit-5", "Hit 5", {
    number_count_main: 5, number_range_main: 39, draw_schedule: "Mon, Wed, Sat 8:00 PM PT", ticket_price: 1,
  }),
  stateGame("WA", "match-4", "Match 4", {
    number_count_main: 4, number_range_main: 24, draw_schedule: "Daily 8:00 PM PT", ticket_price: 1,
  }),
  stateGame("WA", "cash-pop", "Cash Pop", {
    number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 8:00 PM PT", ticket_price: 1,
  }),
  stateGame("WA", "lotto", "Lotto", {
    number_count_main: 6, number_range_main: 49, draw_schedule: "Mon, Wed, Sat 8:00 PM PT", ticket_price: 1,
    description: "Washington's Lotto — pick 6 of 49 for a jackpot starting at $1 million.",
  }),
  stateGame("CO", "lotto-plus", "Lotto Plus", {
    number_count_main: 6, number_range_main: 40, draw_schedule: "Wed, Sat 7:35 PM MT", ticket_price: 2,
  }),
  stateGame("CO", "cash-5", "Cash 5", {
    number_count_main: 5, number_range_main: 32, draw_schedule: "Daily 7:35 PM MT",
  }),
  stateGame("DE", "multi-win-lotto", "Multi-Win Lotto", {
    number_count_main: 6, number_range_main: 35, draw_schedule: "Mon, Wed, Fri 7:57 PM ET", ticket_price: 1,
  }),
  stateGame("FL", "jackpot-triple-play", "Jackpot Triple Play", {
    number_count_main: 6, number_range_main: 46, draw_schedule: "Tue, Fri 11:15 PM ET", ticket_price: 1,
  }),
  stateGame("ID", "idaho-cash", "Idaho Cash", {
    number_count_main: 5, number_range_main: 35, draw_schedule: "Mon, Wed, Fri, Sat 8:00 PM MT", ticket_price: 1,
  }),
  // --- Additional state draw games ---
  stateGame("CT", "lotto", "Lotto!", {
    number_count_main: 6, number_range_main: 44, draw_schedule: "Tue, Fri 10:38 PM ET", ticket_price: 1,
    description: "Connecticut Lotto! — pick 6 of 44 for the state's flagship jackpot game.",
  }),
  stateGame("CT", "cash-5", "Cash 5", {
    number_count_main: 5, number_range_main: 35, draw_schedule: "Daily 10:38 PM ET",
  }),

  stateGame("IN", "hoosier-lotto", "Hoosier Lotto", {
    number_count_main: 6, number_range_main: 46, draw_schedule: "Wed, Sat 11:00 PM ET", ticket_price: 1,
  }),
  stateGame("IN", "cash-5", "Cash 5", {
    number_count_main: 5, number_range_main: 45, draw_schedule: "Daily 11:00 PM ET",
  }),
  stateGame("IN", "lotto-plus", "Lotto Plus", {
    number_count_main: 6, number_range_main: 46, draw_schedule: "Wed, Sat 11:00 PM ET", ticket_price: 1,
    description: "Hoosier Lotto add-on that doubles the jackpot for an extra $1.",
  }),
  stateGame("IN", "quick-draw-midday", "Quick Draw Midday", {
    number_count_main: 10, number_range_main: 80, draw_schedule: "Daily 12:39 PM ET",
  }),
  stateGame("IN", "quick-draw-evening", "Quick Draw Evening", {
    number_count_main: 10, number_range_main: 80, draw_schedule: "Daily 11:00 PM ET",
  }),
  stateGame("IN", "cash-pop-morning", "Cash Pop Morning", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 8:30 AM ET", ticket_price: 1 }),
  stateGame("IN", "cash-pop-matinee", "Cash Pop Matinee", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 12:30 PM ET", ticket_price: 1 }),
  stateGame("IN", "cash-pop-afternoon", "Cash Pop Afternoon", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 4:30 PM ET", ticket_price: 1 }),
  stateGame("IN", "cash-pop-evening", "Cash Pop Evening", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 8:30 PM ET", ticket_price: 1 }),
  stateGame("IN", "cash-pop-late-night", "Cash Pop Late Night", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 11:59 PM ET", ticket_price: 1 }),
  stateGame("IL", "luckyday-lotto-midday", "LuckyDay Lotto Midday", {
    number_count_main: 5, number_range_main: 45, draw_schedule: "Daily 12:40 PM CT", ticket_price: 1,
  }),
  stateGame("IL", "luckyday-lotto-evening", "LuckyDay Lotto Evening", {
    number_count_main: 5, number_range_main: 45, draw_schedule: "Daily 9:22 PM CT", ticket_price: 1,
  }),
  stateGame("KS", "super-cash", "Super Cash", {
    number_count_main: 5, number_range_main: 32, number_count_bonus: 1, number_range_bonus: 26,
    draw_schedule: "Mon, Wed, Sat 9:10 PM CT", ticket_price: 1,
  }),
  stateGame("KY", "cash-ball", "Cash Ball", {
    number_count_main: 4, number_range_main: 35, number_count_bonus: 1, number_range_bonus: 4,
    draw_schedule: "Daily 11:00 PM ET",
  }),
  stateGame("LA", "easy-5", "Easy 5", {
    number_count_main: 5, number_range_main: 37, draw_schedule: "Wed, Sat 9:59 PM CT", ticket_price: 1,
  }),
  stateGame("LA", "lotto", "Lotto", {
    number_count_main: 6, number_range_main: 42, draw_schedule: "Wed, Sat 9:59 PM CT", ticket_price: 1,
    description: "Louisiana Lotto — pick 6 of 42 for a jackpot starting at $500,000.",
  }),
  stateGame("MD", "multi-match", "MultiMatch", {
    number_count_main: 6, number_range_main: 43, draw_schedule: "Mon, Thu 7:56 PM ET", ticket_price: 2,
  }),
  stateGame("MD", "bonus-match-5", "Bonus Match 5", {
    number_count_main: 5, number_range_main: 39, number_count_bonus: 1, number_range_bonus: 39,
    draw_schedule: "Daily 7:56 PM ET", ticket_price: 1,
  }),
  stateGame("MD", "cash-pop-9am", "Cash Pop 9am", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 9:00 AM ET", ticket_price: 1 }),
  stateGame("MD", "cash-pop-1pm", "Cash Pop 1pm", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 1:00 PM ET", ticket_price: 1 }),
  stateGame("MD", "cash-pop-6pm", "Cash Pop 6pm", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 6:00 PM ET", ticket_price: 1 }),
  stateGame("MD", "cash-pop-11pm", "Cash Pop 11pm", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 11:00 PM ET", ticket_price: 1 }),
  stateGame("ME", "megabucks-plus", "Megabucks Plus", {
    number_count_main: 6, number_range_main: 41, draw_schedule: "Wed, Sat 7:35 PM ET", ticket_price: 1,
  }),
  stateGame("ME", "gimme-5", "Gimme 5", {
    number_count_main: 5, number_range_main: 39, draw_schedule: "Mon-Fri 6:50 PM ET", ticket_price: 1,
  }),
  stateGame("ME", "cash-pop-early-bird", "Cash Pop Early Bird", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 9:00 AM ET", ticket_price: 1 }),
  stateGame("ME", "cash-pop-brunch", "Cash Pop Brunch", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 11:30 AM ET", ticket_price: 1 }),
  stateGame("ME", "cash-pop-matinee", "Cash Pop Matinee", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 2:00 PM ET", ticket_price: 1 }),
  stateGame("ME", "cash-pop-suppertime", "Cash Pop Suppertime", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 5:30 PM ET", ticket_price: 1 }),
  stateGame("ME", "cash-pop-night-owl", "Cash Pop Night Owl", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 10:00 PM ET", ticket_price: 1 }),
  stateGame("MN", "gopher-5", "Gopher 5", {
    number_count_main: 5, number_range_main: 47, draw_schedule: "Mon, Wed, Fri 10:00 PM CT", ticket_price: 1,
  }),
  stateGame("MN", "northstar-cash", "NORTH5", {
    number_count_main: 5, number_range_main: 31, draw_schedule: "Daily 10:00 PM CT",
  }),
  stateGame("MO", "mo-millions-main", "MO Millions Main", {
    number_count_main: 5, number_range_main: 45, number_count_bonus: 1, number_range_bonus: 18,
    draw_schedule: "Wed, Sat 8:59 PM CT", ticket_price: 2,
    description: "Missouri's in-state jackpot game — pick 5 numbers plus a Millions Ball.",
  }),
  stateGame("MO", "mo-millions-double-play", "MO Millions Double Play", {
    number_count_main: 5, number_range_main: 45, number_count_bonus: 1, number_range_bonus: 18,
    draw_schedule: "Wed, Sat 8:59 PM CT", ticket_price: 1,
    description: "Optional second drawing for MO Millions for additional cash prizes.",
  }),
  stateGame("MO", "cash-pop-early-bird", "Cash Pop Early Bird", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 8:00 AM CT", ticket_price: 1 }),
  stateGame("MO", "cash-pop-late-morning", "Cash Pop Late Morning", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 11:00 AM CT", ticket_price: 1 }),
  stateGame("MO", "cash-pop-matinee", "Cash Pop Matinee", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 2:00 PM CT", ticket_price: 1 }),
  stateGame("MO", "cash-pop-prime-time", "Cash Pop Prime Time", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 7:00 PM CT", ticket_price: 1 }),
  stateGame("MO", "cash-pop-night-owl", "Cash Pop Night Owl", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 11:00 PM CT", ticket_price: 1 }),
  stateGame("MO", "show-me-cash", "Show Me Cash", {
    number_count_main: 5, number_range_main: 39, draw_schedule: "Daily 8:59 PM CT",
  }),
  stateGame("MS", "match-5", "Match 5", {
    number_count_main: 5, number_range_main: 35, draw_schedule: "Daily 9:30 PM CT",
  }),
  stateGame("MS", "cash-pop-midday", "Cash Pop Midday", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 12:30 PM CT", ticket_price: 1 }),
  stateGame("MS", "cash-pop-evening", "Cash Pop Evening", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 8:30 PM CT", ticket_price: 1 }),
  stateGame("SC", "cash-pop-midday", "Cash Pop Midday", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 1:59 PM ET", ticket_price: 1 }),
  stateGame("SC", "cash-pop-evening", "Cash Pop Evening", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 6:59 PM ET", ticket_price: 1 }),
  stateGame("MT", "big-sky-bonus", "Big Sky Bonus", {
    number_count_main: 4, number_range_main: 28, number_count_bonus: 1, number_range_bonus: 17,
    draw_schedule: "Daily 7:30 PM MT",
  }),
  stateGame("MT", "montana-cash", "Montana Cash", {
    number_count_main: 5, number_range_main: 45, draw_schedule: "Wed, Sat 8:00 PM MT", ticket_price: 1,
  }),
  stateGame("NE", "pick-5", "Pick 5", {
    number_count_main: 5, number_range_main: 38, draw_schedule: "Daily 9:00 PM CT",
  }),
  stateGame("NH", "megabucks", "Megabucks Plus", {
    number_count_main: 5, number_range_main: 41, number_count_bonus: 1, number_range_bonus: 6,
    draw_schedule: "Wed, Sat 7:59 PM ET", ticket_price: 2,
  }),
  stateGame("NH", "gimme-5", "Gimme 5", {
    number_count_main: 5, number_range_main: 39, draw_schedule: "Mon-Fri 6:55 PM ET", ticket_price: 1,
  }),
  stateGame("NJ", "jersey-cash-5", "Cash 5", {
    number_count_main: 5, number_range_main: 45, draw_schedule: "Daily 10:57 PM ET",
  }),
  stateGame("NJ", "double-play", "Double Play", {
    number_count_main: 5, number_range_main: 69, number_count_bonus: 1, number_range_bonus: 26,
    draw_schedule: "Mon, Wed, Sat 10:59 PM ET", ticket_price: 1,
    description: "Powerball Double Play add-on giving tickets a second drawing for cash prizes up to $10 million.",
  }),
  stateGame("NJ", "xtra", "Xtra", {
    number_count_main: 0, number_range_main: 0, draw_schedule: "Mon, Thu 10:57 PM ET", ticket_price: 1,
    description: "Add-on that multiplies non-jackpot Jersey draw-game prizes.",
  }),
  stateGame("NM", "roadrunner-cash", "Roadrunner Cash", {
    number_count_main: 5, number_range_main: 37, draw_schedule: "Daily 9:30 PM MT",
  }),
  stateGame("NY", "pick-10", "Pick 10", {
    number_count_main: 10, number_range_main: 80, draw_schedule: "Daily 8:30 PM ET", ticket_price: 1,
  }),
  stateGame("OH", "rolling-cash-5", "Rolling Cash 5", {
    number_count_main: 5, number_range_main: 39, draw_schedule: "Daily 7:35 PM ET",
  }),
  stateGame("OR", "megabucks", "Megabucks", {
    number_count_main: 6, number_range_main: 48, draw_schedule: "Mon, Wed, Sat 7:29 PM PT", ticket_price: 2,
  }),
  stateGame("OR", "win-for-life", "Win for Life", {
    number_count_main: 4, number_range_main: 77, draw_schedule: "Mon, Wed, Sat 7:29 PM PT", ticket_price: 2,
    description: "Match all 4 numbers to win $1,000 a week for life.",
  }),
  stateGame("OK", "cash-5", "Cash 5", {
    number_count_main: 5, number_range_main: 36, draw_schedule: "Mon-Sat 9:59 PM CT", ticket_price: 1,
  }),
  stateGame("PA", "cash-5", "Cash 5", {
    number_count_main: 5, number_range_main: 43, draw_schedule: "Daily 6:59 PM ET",
  }),
  stateGame("PA", "treasure-hunt", "Treasure Hunt", {
    number_count_main: 5, number_range_main: 30, draw_schedule: "Daily 1:35 PM ET", ticket_price: 1,
  }),
  stateGame("PA", "cash-pop-morning-buzz", "Cash Pop Morning Buzz", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 9:00 AM ET", ticket_price: 1 }),
  stateGame("PA", "cash-pop-lunch-break", "Cash Pop Lunch Break", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 12:00 PM ET", ticket_price: 1 }),
  stateGame("PA", "cash-pop-prime-time", "Cash Pop Prime Time", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 6:00 PM ET", ticket_price: 1 }),
  stateGame("PA", "cash-pop-night-owl", "Cash Pop Night Owl", { number_count_main: 1, number_range_main: 15, draw_schedule: "Daily 11:00 PM ET", ticket_price: 1 }),
  stateGame("RI", "wild-money", "Wild Money", {
    number_count_main: 5, number_range_main: 35, draw_schedule: "Daily 6:59 PM ET",
  }),
  stateGame("SC", "palmetto-cash-5", "Palmetto Cash 5", {
    number_count_main: 5, number_range_main: 38, draw_schedule: "Daily 6:59 PM ET",
  }),
  stateGame("SD", "dakota-cash", "Dakota Cash", {
    number_count_main: 5, number_range_main: 35, draw_schedule: "Wed, Sat 9:00 PM CT",
  }),
  stateGame("TN", "tennessee-cash", "Tennessee Cash", {
    number_count_main: 5, number_range_main: 35, number_count_bonus: 1, number_range_bonus: 5,
    draw_schedule: "Mon, Wed, Fri 10:00 PM CT", ticket_price: 1,
  }),
  stateGame("TN", "daily-tennessee-jackpot", "Daily Tennessee Jackpot", {
    number_count_main: 5, number_range_main: 35, draw_schedule: "Daily 10:28 PM CT", ticket_price: 1,
  }),
  stateGame("TX", "two-step", "Texas Two Step", {
    number_count_main: 4, number_range_main: 35, number_count_bonus: 1, number_range_bonus: 35,
    draw_schedule: "Mon, Thu 10:12 PM CT", ticket_price: 1,
  }),
  stateGame("VT", "megabucks-vt", "Megabucks Plus", {
    number_count_main: 5, number_range_main: 41, number_count_bonus: 1, number_range_bonus: 6,
    draw_schedule: "Wed, Sat 7:59 PM ET", ticket_price: 2,
  }),
  stateGame("VT", "gimme-5", "Gimme 5", {
    number_count_main: 5, number_range_main: 39, draw_schedule: "Mon-Fri 6:55 PM ET", ticket_price: 1,
  }),
  stateGame("WI", "megabucks", "Megabucks", {
    number_count_main: 6, number_range_main: 49, draw_schedule: "Wed, Sat 9:00 PM CT", ticket_price: 1,
  }),
  stateGame("WI", "badger-5", "Badger 5", {
    number_count_main: 5, number_range_main: 31, draw_schedule: "Daily 9:00 PM CT",
  }),
  stateGame("WI", "super-cash", "Super Cash", {
    number_count_main: 6, number_range_main: 39, draw_schedule: "Daily 9:00 PM CT", ticket_price: 1,
  }),
  stateGame("WI", "all-or-nothing-midday", "All or Nothing Midday", {
    number_count_main: 11, number_range_main: 22, draw_schedule: "Daily 1:30 PM CT", ticket_price: 1,
  }),
  stateGame("WI", "all-or-nothing-evening", "All or Nothing Evening", {
    number_count_main: 11, number_range_main: 22, draw_schedule: "Daily 9:00 PM CT", ticket_price: 1,
  }),
  stateGame("WV", "cash-25", "Cash 25", {
    number_count_main: 6, number_range_main: 25, draw_schedule: "Wed, Sat 6:59 PM ET", ticket_price: 1,
  }),
  stateGame("WY", "cowboy-draw", "Cowboy Draw", {
    number_count_main: 5, number_range_main: 45, draw_schedule: "Mon, Thu 8:00 PM MT", ticket_price: 1,
  }),
  stateGame("AR", "natural-state-jackpot", "Natural State Jackpot", {
    number_count_main: 5, number_range_main: 39, draw_schedule: "Mon, Wed, Fri 8:30 PM CT", ticket_price: 1,
  }),
  stateGame("AR", "lotto", "Lotto", {
    number_count_main: 6, number_range_main: 40, draw_schedule: "Wed, Sat 8:30 PM CT", ticket_price: 1,
  }),
];

// Attach `states` field to multistate games (null) for consistent shape
/**
 * Verified per-state daily numbers games. Each entry below has been confirmed
 * against the state lottery's official site. We do NOT auto-generate Pick 2 /
 * Pick 3 / Pick 4 across every state — many states do not offer all of those
 * games (e.g. Arizona has no Pick 2 or Pick 4). Add a state's daily game
 * here ONLY after confirming it on the official lottery website.
 */
type DailyDef = {
  suffix: string;
  name: string;
  digits: 2 | 3 | 4 | 5;
  twiceDaily?: boolean; // true → midday + evening variants
  /** Explicit per-draw variants (overrides twiceDaily). */
  draws?: Array<{ sfx: string; lbl: string; sched: string }>;
  price?: number;
};

// Verified daily/numbers games per state (per official lottery sites, 2025).
// Only digit-pick style games (0–9 per position) belong here. State-specific
// jackpot / cash games already live in STATE_GAMES above.
const DAILY_GAMES_BY_STATE: Record<string, DailyDef[]> = {
  AR: [
    { suffix: "cash-3", name: "Cash 3", digits: 3, twiceDaily: true },
    { suffix: "cash-4", name: "Cash 4", digits: 4, twiceDaily: true },
  ],
  AZ: [
    { suffix: "pick-3", name: "Pick 3", digits: 3, twiceDaily: true },
    { suffix: "pick-4", name: "Pick 4", digits: 4, twiceDaily: true },
  ],
  CA: [
    { suffix: "daily-3", name: "Daily 3", digits: 3, twiceDaily: true },
    { suffix: "daily-4", name: "Daily 4", digits: 4 },
  ],
  CO: [{ suffix: "pick-3", name: "Pick 3", digits: 3, twiceDaily: true }],
  CT: [
    {
      suffix: "play3", name: "Play3", digits: 3,
      draws: [
        { sfx: "play3-day", lbl: "Play3 Day", sched: "Daily 1:57 PM ET" },
        { sfx: "play3-night", lbl: "Play3 Night", sched: "Daily 10:29 PM ET" },
      ],
    },
    {
      suffix: "play4", name: "Play4", digits: 4,
      draws: [
        { sfx: "play4-day", lbl: "Play4 Day", sched: "Daily 1:57 PM ET" },
        { sfx: "play4-night", lbl: "Play4 Night", sched: "Daily 10:29 PM ET" },
      ],
    },
  ],
  DC: [
    {
      suffix: "dc-3", name: "DC-3", digits: 3,
      draws: [
        { sfx: "dc-3-1-50pm", lbl: "DC 3 1:50PM", sched: "Daily 1:50 PM ET" },
        { sfx: "dc-3-7-50pm", lbl: "DC 3 7:50PM", sched: "Daily 7:50 PM ET" },
        { sfx: "dc-3-11-30pm", lbl: "DC 3 11:30PM", sched: "Daily 11:30 PM ET" },
      ],
    },
    {
      suffix: "dc-4", name: "DC-4", digits: 4,
      draws: [
        { sfx: "dc-4-1-50pm", lbl: "DC 4 1:50PM", sched: "Daily 1:50 PM ET" },
        { sfx: "dc-4-7-50pm", lbl: "DC 4 7:50PM", sched: "Daily 7:50 PM ET" },
        { sfx: "dc-4-11-30pm", lbl: "DC 4 11:30PM", sched: "Daily 11:30 PM ET" },
      ],
    },
    {
      suffix: "dc-5", name: "DC-5", digits: 5,
      draws: [
        { sfx: "dc-5-1-50pm", lbl: "DC 5 1:50PM", sched: "Daily 1:50 PM ET" },
        { sfx: "dc-5-7-50pm", lbl: "DC 5 7:50PM", sched: "Daily 7:50 PM ET" },
      ],
    },
  ],
  DE: [
    {
      suffix: "play-3", name: "Play 3", digits: 3,
      draws: [
        { sfx: "play-3-day", lbl: "Play 3 Day", sched: "Daily 1:58 PM ET" },
        { sfx: "play-3-night", lbl: "Play 3 Night", sched: "Daily 7:57 PM ET" },
      ],
    },
    {
      suffix: "play-4", name: "Play 4", digits: 4,
      draws: [
        { sfx: "play-4-day", lbl: "Play 4 Day", sched: "Daily 1:58 PM ET" },
        { sfx: "play-4-night", lbl: "Play 4 Night", sched: "Daily 7:57 PM ET" },
      ],
    },
    {
      suffix: "play-5", name: "Play 5", digits: 5,
      draws: [
        { sfx: "play-5-day", lbl: "Play 5 Day", sched: "Daily 1:58 PM ET" },
        { sfx: "play-5-night", lbl: "Play 5 Night", sched: "Daily 7:57 PM ET" },
      ],
    },
  ],
  FL: [
    { suffix: "pick-2", name: "Pick 2", digits: 2, twiceDaily: true },
    { suffix: "pick-3", name: "Pick 3", digits: 3, twiceDaily: true },
    { suffix: "pick-4", name: "Pick 4", digits: 4, twiceDaily: true },
    { suffix: "pick-5", name: "Pick 5", digits: 5, twiceDaily: true },
  ],
  GA: [
    {
      suffix: "cash-3", name: "Cash 3", digits: 3,
      draws: [
        { sfx: "cash-3-midday", lbl: "Cash 3 Midday", sched: "Daily Midday" },
        { sfx: "cash-3-evening", lbl: "Cash 3 Evening", sched: "Daily Evening" },
        { sfx: "cash-3-night", lbl: "Cash 3 Night", sched: "Daily 11:34 PM ET" },
      ],
    },
    {
      suffix: "cash-4", name: "Cash 4", digits: 4,
      draws: [
        { sfx: "cash-4-midday", lbl: "Cash 4 Midday", sched: "Daily Midday" },
        { sfx: "cash-4-evening", lbl: "Cash 4 Evening", sched: "Daily Evening" },
        { sfx: "cash-4-night", lbl: "Cash 4 Night", sched: "Daily 11:34 PM ET" },
      ],
    },
    { suffix: "georgia-five", name: "Georgia FIVE", digits: 5, twiceDaily: true },
  ],
  IA: [
    { suffix: "pick-3", name: "Pick 3", digits: 3, twiceDaily: true },
    { suffix: "pick-4", name: "Pick 4", digits: 4, twiceDaily: true },
  ],
  ID: [
    {
      suffix: "pick-3", name: "Pick 3", digits: 3,
      draws: [
        { sfx: "pick-3-day", lbl: "Pick 3 Day", sched: "Daily 1:59 PM MT" },
        { sfx: "pick-3-night", lbl: "Pick 3 Night", sched: "Daily 7:59 PM MT" },
      ],
    },
    {
      suffix: "pick-4", name: "Pick 4", digits: 4,
      draws: [
        { sfx: "pick-4-day", lbl: "Pick 4 Day", sched: "Daily 1:59 PM MT" },
        { sfx: "pick-4-night", lbl: "Pick 4 Night", sched: "Daily 7:59 PM MT" },
      ],
    },
  ],
  IL: [
    { suffix: "pick-3", name: "Pick 3", digits: 3, twiceDaily: true },
    { suffix: "pick-4", name: "Pick 4", digits: 4, twiceDaily: true },
  ],
  IN: [
    { suffix: "daily-3", name: "Daily 3", digits: 3, twiceDaily: true },
    { suffix: "daily-4", name: "Daily 4", digits: 4, twiceDaily: true },
  ],
  KS: [{ suffix: "pick-3", name: "Pick 3", digits: 3, twiceDaily: true }],
  KY: [
    { suffix: "pick-3", name: "Pick 3", digits: 3, twiceDaily: true },
    { suffix: "pick-4", name: "Pick 4", digits: 4, twiceDaily: true },
  ],
  LA: [
    { suffix: "pick-3", name: "Pick 3", digits: 3 },
    { suffix: "pick-4", name: "Pick 4", digits: 4 },
    { suffix: "pick-5", name: "Pick 5", digits: 5 },
  ],
  MD: [
    { suffix: "pick-3", name: "Pick 3", digits: 3, twiceDaily: true },
    { suffix: "pick-4", name: "Pick 4", digits: 4, twiceDaily: true },
    { suffix: "pick-5", name: "Pick 5", digits: 5, twiceDaily: true },
  ],
  ME: [
    {
      suffix: "pick-3", name: "Pick 3", digits: 3,
      draws: [
        { sfx: "pick-3-day", lbl: "Pick 3 Day", sched: "Daily 1:10 PM ET" },
        { sfx: "pick-3-evening", lbl: "Pick 3 Evening", sched: "Daily 6:50 PM ET" },
      ],
    },
    {
      suffix: "pick-4", name: "Pick 4", digits: 4,
      draws: [
        { sfx: "pick-4-day", lbl: "Pick 4 Day", sched: "Daily 1:10 PM ET" },
        { sfx: "pick-4-evening", lbl: "Pick 4 Evening", sched: "Daily 6:50 PM ET" },
      ],
    },
  ],
  MI: [
    { suffix: "daily-3", name: "Daily 3", digits: 3, twiceDaily: true },
    { suffix: "daily-4", name: "Daily 4", digits: 4, twiceDaily: true },
  ],
  MN: [{ suffix: "pick-3", name: "Pick 3", digits: 3 }],
  MA: [{ suffix: "numbers", name: "Numbers", digits: 4, twiceDaily: true }],
  MS: [
    { suffix: "cash-3", name: "Cash 3", digits: 3, twiceDaily: true },
    { suffix: "cash-4", name: "Cash 4", digits: 4, twiceDaily: true },
  ],
  MO: [
    {
      suffix: "pick-3", name: "Pick 3", digits: 3,
      draws: [
        { sfx: "pick-3-midday", lbl: "Pick 3 Midday", sched: "Daily 12:45 PM CT" },
        { sfx: "pick-3-evening", lbl: "Pick 3 Evening", sched: "Daily 8:59 PM CT" },
        { sfx: "pick-3-midday-wild", lbl: "Pick 3 Midday Wild", sched: "Daily 12:45 PM CT" },
        { sfx: "pick-3-evening-wild", lbl: "Pick 3 Evening Wild", sched: "Daily 8:59 PM CT" },
      ],
    },
    {
      suffix: "pick-4", name: "Pick 4", digits: 4,
      draws: [
        { sfx: "pick-4-midday", lbl: "Pick 4 Midday", sched: "Daily 12:45 PM CT" },
        { sfx: "pick-4-evening", lbl: "Pick 4 Evening", sched: "Daily 8:59 PM CT" },
        { sfx: "pick-4-midday-wild", lbl: "Pick 4 Midday Wild", sched: "Daily 12:45 PM CT" },
        { sfx: "pick-4-evening-wild", lbl: "Pick 4 Evening Wild", sched: "Daily 8:59 PM CT" },
      ],
    },
  ],
  NC: [
    {
      suffix: "pick-3", name: "Pick 3", digits: 3,
      draws: [
        { sfx: "pick-3-day", lbl: "Pick 3 Day", sched: "Daily 3:00 PM ET" },
        { sfx: "pick-3-evening", lbl: "Pick 3 Evening", sched: "Daily 11:22 PM ET" },
      ],
    },
    {
      suffix: "pick-4", name: "Pick 4", digits: 4,
      draws: [
        { sfx: "pick-4-day", lbl: "Pick 4 Day", sched: "Daily 3:00 PM ET" },
        { sfx: "pick-4-evening", lbl: "Pick 4 Evening", sched: "Daily 11:22 PM ET" },
      ],
    },
  ],
  NE: [
    { suffix: "pick-3", name: "Pick 3", digits: 3 },
    { suffix: "pick-4", name: "Pick 4", digits: 4 },
    { suffix: "pick-5", name: "Pick 5", digits: 5 },
  ],
  NH: [
    {
      suffix: "pick-3", name: "Pick 3", digits: 3,
      draws: [
        { sfx: "pick-3-day", lbl: "Pick 3 Day", sched: "Daily 1:10 PM ET" },
        { sfx: "pick-3-evening", lbl: "Pick 3 Evening", sched: "Daily 6:55 PM ET" },
      ],
    },
    {
      suffix: "pick-4", name: "Pick 4", digits: 4,
      draws: [
        { sfx: "pick-4-day", lbl: "Pick 4 Day", sched: "Daily 1:10 PM ET" },
        { sfx: "pick-4-evening", lbl: "Pick 4 Evening", sched: "Daily 6:55 PM ET" },
      ],
    },
  ],
  NJ: [
    { suffix: "pick-3", name: "Pick 3", digits: 3, twiceDaily: true },
    { suffix: "pick-4", name: "Pick 4", digits: 4, twiceDaily: true },
  ],
  NM: [
    {
      suffix: "pick-3", name: "Pick 3", digits: 3,
      draws: [
        { sfx: "pick-3-day", lbl: "Pick 3 Day", sched: "Daily 1:30 PM MT" },
        { sfx: "pick-3-evening", lbl: "Pick 3 Evening", sched: "Daily 9:30 PM MT" },
      ],
    },
    {
      suffix: "pick-4", name: "Pick 4", digits: 4,
      draws: [
        { sfx: "pick-4-day", lbl: "Pick 4 Day", sched: "Daily 1:30 PM MT" },
        { sfx: "pick-4-evening", lbl: "Pick 4 Evening", sched: "Daily 9:30 PM MT" },
      ],
    },
  ],
  NY: [
    { suffix: "numbers", name: "Numbers", digits: 3, twiceDaily: true },
    { suffix: "win-4", name: "Win 4", digits: 4, twiceDaily: true },
  ],
  OH: [
    { suffix: "pick-3", name: "Pick 3", digits: 3, twiceDaily: true },
    { suffix: "pick-4", name: "Pick 4", digits: 4, twiceDaily: true },
    { suffix: "pick-5", name: "Pick 5", digits: 5, twiceDaily: true },
  ],
  OK: [{ suffix: "pick-3", name: "Pick 3", digits: 3 }],
  OR: [
    {
      suffix: "pick-4", name: "Pick 4", digits: 4,
      draws: [
        { sfx: "pick-4-1pm", lbl: "Pick 4 1PM", sched: "Daily 1:00 PM PT" },
        { sfx: "pick-4-4pm", lbl: "Pick 4 4PM", sched: "Daily 4:00 PM PT" },
        { sfx: "pick-4-7pm", lbl: "Pick 4 7PM", sched: "Daily 7:00 PM PT" },
        { sfx: "pick-4-10pm", lbl: "Pick 4 10PM", sched: "Daily 10:00 PM PT" },
      ],
    },
  ],
  PA: [
    { suffix: "pick-2", name: "PICK 2", digits: 2, twiceDaily: true },
    { suffix: "pick-3", name: "PICK 3", digits: 3, twiceDaily: true },
    { suffix: "pick-4", name: "PICK 4", digits: 4, twiceDaily: true },
    { suffix: "pick-5", name: "PICK 5", digits: 5, twiceDaily: true },
  ],
  RI: [{ suffix: "numbers", name: "Numbers", digits: 4, twiceDaily: true }],
  SC: [
    { suffix: "pick-3", name: "Pick 3", digits: 3, twiceDaily: true },
    { suffix: "pick-4", name: "Pick 4", digits: 4, twiceDaily: true },
  ],
  TN: [
    {
      suffix: "cash-3", name: "Cash 3", digits: 3,
      draws: [
        { sfx: "cash-3-morning", lbl: "Cash 3 Morning", sched: "Daily 9:28 AM CT" },
        { sfx: "cash-3-midday", lbl: "Cash 3 Midday", sched: "Daily 12:28 PM CT" },
        { sfx: "cash-3-evening", lbl: "Cash 3 Evening", sched: "Daily 6:28 PM CT" },
      ],
    },
    {
      suffix: "cash-4", name: "Cash 4", digits: 4,
      draws: [
        { sfx: "cash-4-morning", lbl: "Cash 4 Morning", sched: "Daily 9:28 AM CT" },
        { sfx: "cash-4-midday", lbl: "Cash 4 Midday", sched: "Daily 12:28 PM CT" },
        { sfx: "cash-4-evening", lbl: "Cash 4 Evening", sched: "Daily 6:28 PM CT" },
      ],
    },
  ],
  TX: [
    {
      suffix: "pick-3", name: "Pick 3", digits: 3,
      draws: [
        { sfx: "pick-3-morning", lbl: "Pick 3 Morning", sched: "Mon-Sat 10:00 AM CT" },
        { sfx: "pick-3-day", lbl: "Pick 3 Day", sched: "Mon-Sat 12:27 PM CT" },
        { sfx: "pick-3-evening", lbl: "Pick 3 Evening", sched: "Mon-Sat 6:00 PM CT" },
        { sfx: "pick-3-night", lbl: "Pick 3 Night", sched: "Mon-Sat 10:12 PM CT" },
      ],
    },
    {
      suffix: "daily-4", name: "Daily 4", digits: 4,
      draws: [
        { sfx: "daily-4-morning", lbl: "Daily 4 Morning", sched: "Mon-Sat 10:00 AM CT" },
        { sfx: "daily-4-day", lbl: "Daily 4 Day", sched: "Mon-Sat 12:27 PM CT" },
        { sfx: "daily-4-evening", lbl: "Daily 4 Evening", sched: "Mon-Sat 6:00 PM CT" },
        { sfx: "daily-4-night", lbl: "Daily 4 Night", sched: "Mon-Sat 10:12 PM CT" },
      ],
    },
  ],
  VA: [
    {
      suffix: "pick-3", name: "Pick 3", digits: 3,
      draws: [
        { sfx: "pick-3-day", lbl: "Pick 3 Day", sched: "Daily 1:59 PM ET" },
        { sfx: "pick-3-night", lbl: "Pick 3 Night", sched: "Daily 11:00 PM ET" },
      ],
    },
    {
      suffix: "pick-4", name: "Pick 4", digits: 4,
      draws: [
        { sfx: "pick-4-day", lbl: "Pick 4 Day", sched: "Daily 1:59 PM ET" },
        { sfx: "pick-4-night", lbl: "Pick 4 Night", sched: "Daily 11:00 PM ET" },
      ],
    },
    {
      suffix: "pick-5", name: "Pick 5", digits: 5,
      draws: [
        { sfx: "pick-5-day", lbl: "Pick 5 Day", sched: "Daily 1:59 PM ET" },
        { sfx: "pick-5-night", lbl: "Pick 5 Night", sched: "Daily 11:00 PM ET" },
      ],
    },
  ],
  VT: [
    {
      suffix: "pick-3", name: "Pick 3", digits: 3,
      draws: [
        { sfx: "pick-3-day", lbl: "Pick 3 Day", sched: "Daily 1:10 PM ET" },
        { sfx: "pick-3-evening", lbl: "Pick 3 Evening", sched: "Daily 6:55 PM ET" },
      ],
    },
    {
      suffix: "pick-4", name: "Pick 4", digits: 4,
      draws: [
        { sfx: "pick-4-day", lbl: "Pick 4 Day", sched: "Daily 1:10 PM ET" },
        { sfx: "pick-4-evening", lbl: "Pick 4 Evening", sched: "Daily 6:55 PM ET" },
      ],
    },
  ],
  WI: [
    { suffix: "pick-3", name: "Pick 3", digits: 3, twiceDaily: true },
    { suffix: "pick-4", name: "Pick 4", digits: 4, twiceDaily: true },
  ],
  WA: [
    { suffix: "pick-3", name: "Pick 3", digits: 3 },
  ],
  WV: [
    { suffix: "daily-3", name: "Daily 3", digits: 3 },
    { suffix: "daily-4", name: "Daily 4", digits: 4 },
  ],
};

const VERIFIED_DAILY_GAMES: Game[] = Object.entries(DAILY_GAMES_BY_STATE).flatMap(
  ([abbr, defs]) =>
    defs.flatMap((d) => {
      const variants = d.twiceDaily
        ? [
            { sfx: `${d.suffix}-midday`, lbl: `${d.name} Midday`, sched: "Daily Midday" },
            { sfx: `${d.suffix}-evening`, lbl: `${d.name} Evening`, sched: "Daily Evening" },
          ]
        : [{ sfx: d.suffix, lbl: d.name, sched: "Daily" }];
      const list = d.draws ?? variants;
      return list.map((v) =>
        stateGame(abbr, v.sfx, v.lbl, {
          number_count_main: d.digits,
          number_range_main: 9,
          draw_schedule: v.sched,
          ticket_price: d.price ?? 0.5,
        }),
      );
    }),
);

// De-duplicate against STATE_GAMES (existing curated entries win).
const _existingSlugs = new Set(STATE_GAMES.map((g) => g.slug));
const _dedupedDaily = VERIFIED_DAILY_GAMES.filter((g) => !_existingSlugs.has(g.slug));

export const ALL_GAMES: Game[] = [
  ...MULTISTATE_GAMES.map((g) => ({ ...g, states: undefined })),
  ...STATE_GAMES,
  ..._dedupedDaily,
];